import React, { useContext, useEffect, useRef, useState } from "react";
import { makeStyles } from "@mui/styles";
import Paper from "@mui/material/Paper";
import SearchIcon from "@mui/icons-material/Search";
import InputBase from "@mui/material/InputBase";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Badge from "@mui/material/Badge";
import PersonIcon from "@mui/icons-material/Person";
import ForumIcon from "@mui/icons-material/Forum";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import NewTicketModal from "../NewTicketModal";
import TicketsList from "../TicketsList";
import TabPanel from "../TabPanel";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../Can";
import TicketsQueueSelect from "../TicketsQueueSelect";
import { Button, Tooltip, IconButton, Select, MenuItem as MuiMenuItem, FormControl, InputLabel } from "@mui/material";
import DownloadIcon from "@mui/icons-material/GetApp";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  ticketsWrapper: {
    position: "relative",
    display: "flex",
    height: "100%",
    flexDirection: "column",
    overflow: "hidden",
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
  },
  tabsHeader: {
    flex: "none",
    backgroundColor: theme.palette.background.paper,
  },
  tab: {
    minWidth: 90,
    width: 90,
  },
  ticketOptionsBox: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    background: theme.palette.background.paper,
    padding: theme.spacing(1),
  },
  serachInputWrapper: {
    flex: 1,
    background: theme.palette.background.default,
    display: "flex",
    borderRadius: 40,
    padding: 4,
    marginRight: theme.spacing(1),
  },
  searchIcon: {
    color: "grey",
    marginLeft: 6,
    marginRight: 6,
    alignSelf: "center",
  },
  searchInput: {
    flex: 1,
    border: "none",
    borderRadius: 30,
    color: theme.palette.text.primary,
    backgroundColor: theme.palette.background.default,
  },
  badge: {
    right: "-10px",
  },
}));

const TicketsManager = () => {
  const classes = useStyles();
  const [searchParam, setSearchParam] = useState("");
  const [tab, setTab] = useState("personal");
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);

  const handleExportCSV = async () => {
    try {
      const resp = await api.get("/tickets/export/csv", {
        params: { status: tab === "personal" ? "open" : tab === "pending" ? "pending" : "closed" },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([resp.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `tickets-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {}
  };
  const [showAllTickets, setShowAllTickets] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState("");
  const [allUsers, setAllUsers] = useState([]);
  const searchInputRef = useRef();
  const { user } = useContext(AuthContext);
  const [personalCount, setPersonalCount] = useState(0);
  const [generalCount, setGeneralCount] = useState(0);
  const isPrivileged = user.profile === "admin" || user.profile === "supervisor";
  const userQueueIds = user.queues.map((q) => q.id);
  const [allQueues, setAllQueues] = useState([]);
  const [selectedQueueIds, setSelectedQueueIds] = useState(userQueueIds || []);
  useEffect(() => {
    if (isPrivileged) {
      api.get("/queue").then(({ data }) => {
        setAllQueues(data);
        setSelectedQueueIds(data.map((q) => q.id));
      }).catch(() => {});
      api.get("/users/", { params: { limit: 100 } })
        .then(({ data }) => setAllUsers(data.users || []))
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (tab === "search") {
      searchInputRef.current.focus();
      setSearchParam("");
    }
  }, [tab]);

  useEffect(() => {
    const handler = (e) => {
      if (e.detail && typeof e.detail === "string") setTab(e.detail);
    };
    window.addEventListener("whaticket:switch-tab", handler);
    return () => window.removeEventListener("whaticket:switch-tab", handler);
  }, []);

  let searchTimeout;

  const handleSearch = (e) => {
    const searchedTerm = e.target.value.toLowerCase();
    clearTimeout(searchTimeout);
    if (searchedTerm === "") {
      setSearchParam(searchedTerm);
      setTab("personal");
      return;
    }
    searchTimeout = setTimeout(() => {
      setSearchParam(searchedTerm);
    }, 500);
  };

  const handleChangeTab = (e, newValue) => {
    setTab(newValue);
  };

  return (
    <Paper elevation={0} variant="outlined" className={classes.ticketsWrapper}>
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        onClose={() => setNewTicketModalOpen(false)}
      />
      <Paper elevation={0} square className={classes.tabsHeader}>
        <Tabs
          value={tab}
          onChange={handleChangeTab}
          variant="fullWidth"
          indicatorColor="primary"
          textColor="primary"
        >
          <Tab
            value="personal"
            icon={<PersonIcon />}
            label={
              <Badge className={classes.badge} badgeContent={personalCount || null} color="primary">
                {i18n.t("tickets.tabs.personal.title")}
              </Badge>
            }
            classes={{ root: classes.tab }}
          />
          <Tab
            value="general"
            icon={<ForumIcon />}
            label={
              <Badge className={classes.badge} badgeContent={generalCount} color="secondary">
                {i18n.t("tickets.tabs.general.title")}
              </Badge>
            }
            classes={{ root: classes.tab }}
          />
          <Tab
            value="closed"
            icon={<CheckBoxIcon />}
            label={i18n.t("tickets.tabs.closed.title")}
            classes={{ root: classes.tab }}
          />
          <Tab
            value="search"
            icon={<SearchIcon />}
            label={i18n.t("tickets.tabs.search.title")}
            classes={{ root: classes.tab }}
          />
        </Tabs>
      </Paper>
      <Paper square elevation={0} className={classes.ticketOptionsBox}>
        {tab === "search" ? (
          <div className={classes.serachInputWrapper}>
            <SearchIcon className={classes.searchIcon} />
            <InputBase
              className={classes.searchInput}
              inputRef={searchInputRef}
              placeholder={i18n.t("tickets.search.placeholder")}
              type="search"
              onChange={handleSearch}
            />
          </div>
        ) : (
          <>
            <Tooltip title="Exportar CSV">
              <IconButton size="small" onClick={handleExportCSV} style={{ marginRight: 4 }}>
                <DownloadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setNewTicketModalOpen(true)}
            >
              {i18n.t("ticketsManager.buttons.newTicket")}
            </Button>
            {tab === "personal" && (
              <Can
                role={user.profile}
                perform="tickets-manager:showall"
                yes={() => (
                  <>
                    <FormControlLabel
                      label={i18n.t("tickets.buttons.showAll")}
                      labelPlacement="start"
                      control={
                        <Switch
                          size="small"
                          checked={showAllTickets}
                          onChange={() => { setShowAllTickets((prev) => !prev); setSelectedAgentId(""); }}
                          name="showAllTickets"
                          color="primary"
                        />
                      }
                    />
                    {showAllTickets && (
                      <FormControl size="small" style={{ minWidth: 120, marginLeft: 6 }}>
                        <InputLabel>Atendente</InputLabel>
                        <Select
                          value={selectedAgentId}
                          label="Atendente"
                          onChange={e => setSelectedAgentId(e.target.value)}
                        >
                          <MuiMenuItem value="">Todos</MuiMenuItem>
                          {allUsers.map(u => (
                            <MuiMenuItem key={u.id} value={String(u.id)}>{u.name}</MuiMenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </>
                )}
              />
            )}
          </>
        )}
        <TicketsQueueSelect
          style={{ marginLeft: 6 }}
          selectedQueueIds={selectedQueueIds}
          userQueues={isPrivileged ? allQueues : user?.queues}
          onChange={(values) => setSelectedQueueIds(values)}
        />
      </Paper>

      <TabPanel value={tab} name="personal" className={classes.ticketsWrapper}>
        <TicketsList
          status="open"
          showAll={showAllTickets}
          selectedQueueIds={selectedQueueIds}
          agentId={showAllTickets && selectedAgentId ? selectedAgentId : undefined}
          updateCount={(val) => setPersonalCount(val)}
        />
      </TabPanel>

      <TabPanel value={tab} name="general" className={classes.ticketsWrapper}>
        <TicketsList
          status="pending"
          showAll="true"
          unassigned="true"
          selectedQueueIds={selectedQueueIds}
          updateCount={(val) => setGeneralCount(val)}
        />
      </TabPanel>

      <TabPanel value={tab} name="closed" className={classes.ticketsWrapper}>
        <TicketsList
          status="closed"
          showAll={true}
          selectedQueueIds={selectedQueueIds}
        />
      </TabPanel>

      <TabPanel value={tab} name="search" className={classes.ticketsWrapper}>
        <TicketsList
          searchParam={searchParam}
          showAll={true}
          selectedQueueIds={selectedQueueIds}
        />
      </TabPanel>
    </Paper>
  );
};

export default TicketsManager;
